// NegativeExponentBasicTask.js
// Ujemny wykładnik: a^{-k} = 1/a^{k}
// Zakresy: a∈{2..9}, k: dla a=2 → 1..7; a=3 → 1..4; pozostałe → 1..3

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const factorLatex = (a, exp) => (exp === 1 ? `${a}` : `${a}^{${exp}}`);

function parseUser(raw) {
  if (!raw) return null;
  const s = raw.replace(/\s/g, "");

  // 1) 1/a^k
  let m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return { form: "fracPow", base: parseInt(m[1], 10), exp: parseInt(m[2], 10) };

  // 2) 1/(a^k)
  m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return { form: "fracPowParen", base: parseInt(m[1], 10), exp: parseInt(m[2], 10) };

  // 3) 1/x  (gdzie x może być np. 64 — akceptujemy, jeśli x = a^k)
  m = s.match(/^1\/(\d+)$/);
  if (m) return { form: "fracPlain", denom: parseInt(m[1], 10) };

  // 4) a^-k  (równoważny zapis)
  m = s.match(/^(\d+)\^\-(\d+)$/);
  if (m) return { form: "negPow", base: parseInt(m[1], 10), exp: parseInt(m[2], 10) };

  return null;
}

export function generateTask() {
  const base = pick([2, 3, 4, 5, 6, 7, 8, 9]); // bez 1
  let maxK = 3;
  if (base === 2) maxK = 7;
  else if (base === 3) maxK = 4;

  const k = pick(Array.from({ length: maxK }, (_, i) => i + 1)); // 1..maxK
  const exponent = -k;

  const leftLatex = `${factorLatex(base, exponent)}`; // np. 8^{-2}
  return { type: "NegativeExponentBasicTask", data: { base, k, exponent, leftLatex } };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      Oblicz: <InlineMath math={data.leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  const { base, k } = task.data;
  const ph = `1/${Math.pow(base, k)}`; // podpowiadamy docelowy format liczbowy
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder={`np. 1/4 lub 0,25`}
        label=""
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parseUser(userAnswer.raw);
  if (!parsed) return false;

  const { base, k } = data;
  const targetDen = Math.pow(base, k);

  switch (parsed.form) {
    case "fracPow":
    case "fracPowParen":
      return parsed.base === base && parsed.exp === k;
    case "negPow":
      return parsed.base === base && parsed.exp === k;
    case "fracPlain":
      return parsed.denom === targetDen;
    default:
      return false;
  }
}

export function renderUserAnswer(userAnswer, task) {
  // W tym zadaniu wyświetlamy zawsze docelowy ułamek 1/den (spójnie z instrukcją).
  if (!task || !task.data) return <i>(brak odpowiedzi)</i>;
  const { base, k } = task.data;
  const denom = Math.pow(base, k);
  return <InlineMath math={`\\dfrac{1}{${denom}}`} />;
}


export function renderAnswer({ data }) {
  const { base, k } = data;
  const denom = Math.pow(base, k);
  return <InlineMath math={`\\dfrac{1}{${denom}}`} />;
}

export function renderExplanation({ data }) {
  const { base, k, leftLatex } = data;
  const denom = Math.pow(base, k);
  const rightLatexStep1 = k === 1 ? `\\dfrac{1}{${base}}` : `\\dfrac{1}{${base}^{${k}}}`;
  const rightLatexFinal = `\\dfrac{1}{${denom}}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Skorzystaj z definicji ujemnego wykładnika:</p>
      <BlockMath math={'a^{-n}=\\dfrac{1}{a^{n}}\\quad (a\\neq 0,\\; n\\in\\mathbb{N})'} />
      <p><strong>Krok 2:</strong> Zastosuj do naszego wyrażenia:</p>
      <BlockMath math={`${leftLatex}\\;=\\;${rightLatexStep1}`} />
      <p><strong>Krok 3:</strong> Oblicz potęgę w mianowniku:</p>
      <BlockMath math={`${rightLatexStep1}\\;=\\;${rightLatexFinal}`} />
    </div>
  );
}
